import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A confirmation modal component that returns a promise.
 *
 * Usage:
 *   const confirmed = await window.confirmModal.show({
 *     title: 'Remove Athlete',
 *     message: 'Are you sure you want to remove this athlete from your cart?',
 *     confirmText: 'Remove',
 *     cancelText: 'Keep',
 *     variant: 'danger'
 *   });
 *
 * @typedef {object} ConfirmModalOptions
 * @property {string} [title] - Modal title
 * @property {string} [message] - Modal message
 * @property {string} [confirmText] - Confirm button text
 * @property {string} [cancelText] - Cancel button text
 * @property {'default' | 'danger' | 'warning'} [variant] - Visual variant
 *
 * @typedef {object} ConfirmModalRefs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLElement} title - Title element
 * @property {HTMLElement} message - Message element
 * @property {HTMLButtonElement} confirmButton - Confirm button
 * @property {HTMLButtonElement} cancelButton - Cancel button
 *
 * @extends {Component<ConfirmModalRefs>}
 */
class ConfirmModalComponent extends Component {
  requiredRefs = ['dialog', 'title', 'message', 'confirmButton', 'cancelButton'];

  /** @type {((value: boolean) => void) | null} */
  #resolvePromise = null;

  /** @type {number} */
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    // Expose globally for easy access
    window.confirmModal = {
      show: this.show.bind(this),
    };
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (window.confirmModal) {
      delete window.confirmModal;
    }
  }

  /**
   * Shows the confirmation modal and returns a promise.
   *
   * @param {ConfirmModalOptions} [options] - Modal options
   * @returns {Promise<boolean>} - Resolves true if confirmed, false if cancelled
   */
  show(options = {}) {
    const {
      title = 'Confirm',
      message = 'Are you sure?',
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      variant = 'default',
    } = options;

    // Update content
    this.refs.title.textContent = title;
    this.refs.message.textContent = message;
    this.refs.confirmButton.textContent = confirmText;
    this.refs.cancelButton.textContent = cancelText;

    // Update variant class on the dialog
    const { dialog } = this.refs;
    dialog.classList.remove('confirm-modal--danger', 'confirm-modal--warning');
    if (variant !== 'default') {
      dialog.classList.add(`confirm-modal--${variant}`);
    }

    // Show modal
    this.#showDialog();

    // Return promise
    return new Promise((_resolve) => {
      this.#resolvePromise = _resolve;
    });
  }

  /**
   * Handle confirm button click.
   */
  onConfirm() {
    this.#closeAndResolve(true);
  }

  /**
   * Handle cancel button click.
   */
  onCancel() {
    this.#closeAndResolve(false);
  }

  /**
   * Shows the dialog.
   */
  #showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const _scrollY = window.scrollY;
    this.#previousScrollY = _scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${_scrollY}px`;

      dialog.showModal();

      // Focus the cancel button by default (safer option)
      this.refs.cancelButton.focus();
    });
  }

  /**
   * Closes the dialog.
   *
   * @returns {Promise<void>}
   */
  async closeDialog() {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, { subtree: false });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
  }

  /**
   * Close modal and resolve promise.
   *
   * @param {boolean} _result - Result to resolve with
   */
  async #closeAndResolve(_result) {
    await this.closeDialog();

    if (this.#resolvePromise) {
      this.#resolvePromise(_result);
      this.#resolvePromise = null;
    }
  }
}

if (!customElements.get('confirm-modal-component')) {
  customElements.define('confirm-modal-component', ConfirmModalComponent);
}
